/**
 * Kinematic Benchmarks Configuration
 * 
 * Defines ideal benchmark bands for kinematic metrics by:
 * - Movement type (Instep Shot, Driven Pass, Short Push Pass, Goalkeeper Punt)
 * - Age band (U9–U11, U12–U14, U15–U17, U18+)
 * - Level (Development, Standard, Elite)
 * 
 * IMPORTANT: All knee metrics are stored as FLEXION degrees (not internal angles).
 * If internal angles are computed (0..180), convert: flexion_deg = 180.0 - internal_angle_deg
 */

export type KinematicParamKey =
  | "backswing_knee_flexion_deg"
  | "plant_knee_flexion_deg"
  | "trunk_forward_lean_deg"
  | "hip_rotation_deg"
  | "approach_angle_deg"
  | "foot_speed_mps"
  | "contact_time_ms"
  | "follow_through_angle_deg";

export type ConfidenceTag = "HIGH" | "MEDIUM" | "PROXY" | "PLACEHOLDER";

export interface ParamRange {
  low: number;
  high: number;
}

export interface MetricDefinition {
  label: string;
  unit: string;
  confidenceTag: ConfidenceTag;
  shortTooltip: string;
}

export interface BenchmarkSet {
  movementName: string;
  ageBand: string;
  level: "Development" | "Standard" | "Elite";
  params: Record<KinematicParamKey, ParamRange>;
}

export type MetricWeights = Record<KinematicParamKey, number>;

/**
 * Metric definitions with labels, units, confidence tags, and tooltips
 */
export const METRIC_DEFS: Record<KinematicParamKey, MetricDefinition> = {
  backswing_knee_flexion_deg: {
    label: "Backswing Knee Flexion",
    unit: "°",
    confidenceTag: "HIGH",
    shortTooltip: "Knee bend during backswing phase; affects power generation and technique quality."
  },
  plant_knee_flexion_deg: {
    label: "Plant Knee Flexion",
    unit: "°",
    confidenceTag: "HIGH",
    shortTooltip: "Support leg knee bend at contact; critical for stability and power transfer."
  },
  trunk_forward_lean_deg: {
    label: "Trunk Forward Lean",
    unit: "°",
    confidenceTag: "HIGH",
    shortTooltip: "Body lean angle at contact; influences power, balance, and ball trajectory."
  },
  hip_rotation_deg: {
    label: "Hip Rotation",
    unit: "°",
    confidenceTag: "PROXY",
    shortTooltip: "Estimated hip rotation from 2D view; indicates body rotation for power generation (approximation)."
  },
  approach_angle_deg: {
    label: "Approach Angle",
    unit: "°",
    confidenceTag: "MEDIUM",
    shortTooltip: "Direction of approach run; affects power and accuracy (depends on camera framing)."
  },
  foot_speed_mps: {
    label: "Foot Speed",
    unit: "m/s",
    confidenceTag: "MEDIUM",
    shortTooltip: "Maximum foot velocity at contact; key indicator of power (requires calibration)."
  },
  contact_time_ms: {
    label: "Contact Time",
    unit: "ms",
    confidenceTag: "PLACEHOLDER",
    shortTooltip: "Estimated ball contact duration; placeholder value from frame rate (not measured)."
  },
  follow_through_angle_deg: {
    label: "Follow-Through Angle",
    unit: "°",
    confidenceTag: "HIGH",
    shortTooltip: "Leg extension angle after contact; indicates technique completion and power transfer."
  }
};

/**
 * Default metric weights (sum to 1.0)
 * Lower weights for PROXY and PLACEHOLDER metrics
 */
export const DEFAULT_METRIC_WEIGHTS: MetricWeights = {
  backswing_knee_flexion_deg: 0.15,
  plant_knee_flexion_deg: 0.15,
  trunk_forward_lean_deg: 0.15,
  hip_rotation_deg: 0.08,  // Reduced for PROXY
  approach_angle_deg: 0.12,  // Reduced for MEDIUM
  foot_speed_mps: 0.15,  // MEDIUM but important
  contact_time_ms: 0.05,  // Reduced for PLACEHOLDER
  follow_through_angle_deg: 0.15
};

/**
 * Benchmark sets for all movement types, age bands, and levels
 * 
 * Values are based on coaching-grade ranges (not lab precision).
 * Knee metrics are in FLEXION degrees (180 - internal_angle).
 */
export const BENCHMARK_SETS: BenchmarkSet[] = [
  // ============================================================
  // INSTEP SHOT – POWER
  // ============================================================
  
  // U9–U11 Development
  {
    movementName: "Instep Shot – Power",
    ageBand: "U9–U11",
    level: "Development",
    params: {
      backswing_knee_flexion_deg: { low: 70, high: 110 },
      plant_knee_flexion_deg: { low: 20, high: 60 },
      trunk_forward_lean_deg: { low: 0, high: 25 },
      hip_rotation_deg: { low: 20, high: 70 },
      approach_angle_deg: { low: 15, high: 50 },
      foot_speed_mps: { low: 10, high: 18 },
      contact_time_ms: { low: 5, high: 20 },
      follow_through_angle_deg: { low: 60, high: 120 }
    }
  },
  
  // U9–U11 Standard
  {
    movementName: "Instep Shot – Power",
    ageBand: "U9–U11",
    level: "Standard",
    params: {
      backswing_knee_flexion_deg: { low: 80, high: 110 },
      plant_knee_flexion_deg: { low: 25, high: 55 },
      trunk_forward_lean_deg: { low: 3, high: 22 },
      hip_rotation_deg: { low: 30, high: 65 },
      approach_angle_deg: { low: 20, high: 45 },
      foot_speed_mps: { low: 12, high: 20 },
      contact_time_ms: { low: 6, high: 18 },
      follow_through_angle_deg: { low: 70, high: 115 }
    }
  },
  
  // U9–U11 Elite
  {
    movementName: "Instep Shot – Power",
    ageBand: "U9–U11",
    level: "Elite",
    params: {
      backswing_knee_flexion_deg: { low: 85, high: 115 },
      plant_knee_flexion_deg: { low: 30, high: 50 },
      trunk_forward_lean_deg: { low: 5, high: 20 },
      hip_rotation_deg: { low: 35, high: 60 },
      approach_angle_deg: { low: 25, high: 45 },
      foot_speed_mps: { low: 14, high: 22 },
      contact_time_ms: { low: 7, high: 15 },
      follow_through_angle_deg: { low: 75, high: 110 }
    }
  },
  
  // U12–U14 Development
  {
    movementName: "Instep Shot – Power",
    ageBand: "U12–U14",
    level: "Development",
    params: {
      backswing_knee_flexion_deg: { low: 75, high: 115 },
      plant_knee_flexion_deg: { low: 22, high: 58 },
      trunk_forward_lean_deg: { low: 0, high: 23 },
      hip_rotation_deg: { low: 25, high: 68 },
      approach_angle_deg: { low: 18, high: 48 },
      foot_speed_mps: { low: 12, high: 20 },
      contact_time_ms: { low: 6, high: 19 },
      follow_through_angle_deg: { low: 65, high: 118 }
    }
  },
  
  // U12–U14 Standard
  {
    movementName: "Instep Shot – Power",
    ageBand: "U12–U14",
    level: "Standard",
    params: {
      backswing_knee_flexion_deg: { low: 82, high: 112 },
      plant_knee_flexion_deg: { low: 28, high: 52 },
      trunk_forward_lean_deg: { low: 4, high: 21 },
      hip_rotation_deg: { low: 32, high: 62 },
      approach_angle_deg: { low: 25, high: 43 },
      foot_speed_mps: { low: 14, high: 21 },
      contact_time_ms: { low: 7, high: 16 },
      follow_through_angle_deg: { low: 72, high: 112 }
    }
  },
  
  // U12–U14 Elite
  {
    movementName: "Instep Shot – Power",
    ageBand: "U12–U14",
    level: "Elite",
    params: {
      backswing_knee_flexion_deg: { low: 87, high: 115 },
      plant_knee_flexion_deg: { low: 30, high: 50 },
      trunk_forward_lean_deg: { low: 5, high: 20 },
      hip_rotation_deg: { low: 35, high: 60 },
      approach_angle_deg: { low: 28, high: 45 },
      foot_speed_mps: { low: 15, high: 23 },
      contact_time_ms: { low: 8, high: 14 },
      follow_through_angle_deg: { low: 78, high: 110 }
    }
  },
  
  // U15–U17 Development
  {
    movementName: "Instep Shot – Power",
    ageBand: "U15–U17",
    level: "Development",
    params: {
      backswing_knee_flexion_deg: { low: 80, high: 115 },
      plant_knee_flexion_deg: { low: 25, high: 55 },
      trunk_forward_lean_deg: { low: 0, high: 22 },
      hip_rotation_deg: { low: 30, high: 65 },
      approach_angle_deg: { low: 20, high: 48 },
      foot_speed_mps: { low: 14, high: 22 },
      contact_time_ms: { low: 7, high: 18 },
      follow_through_angle_deg: { low: 70, high: 115 }
    }
  },
  
  // U15–U17 Standard (baseline)
  {
    movementName: "Instep Shot – Power",
    ageBand: "U15–U17",
    level: "Standard",
    params: {
      backswing_knee_flexion_deg: { low: 85, high: 115 },
      plant_knee_flexion_deg: { low: 30, high: 50 },
      trunk_forward_lean_deg: { low: 5, high: 20 },
      hip_rotation_deg: { low: 35, high: 60 },
      approach_angle_deg: { low: 30, high: 45 },
      foot_speed_mps: { low: 16, high: 23 },
      contact_time_ms: { low: 8, high: 12 },
      follow_through_angle_deg: { low: 80, high: 110 }
    }
  },
  
  // U15–U17 Elite
  {
    movementName: "Instep Shot – Power",
    ageBand: "U15–U17",
    level: "Elite",
    params: {
      backswing_knee_flexion_deg: { low: 88, high: 115 },
      plant_knee_flexion_deg: { low: 32, high: 48 },
      trunk_forward_lean_deg: { low: 7, high: 18 },
      hip_rotation_deg: { low: 38, high: 58 },
      approach_angle_deg: { low: 32, high: 45 },
      foot_speed_mps: { low: 18, high: 25 },
      contact_time_ms: { low: 8, high: 11 },
      follow_through_angle_deg: { low: 85, high: 110 }
    }
  },
  
  // U18+ Development
  {
    movementName: "Instep Shot – Power",
    ageBand: "U18+",
    level: "Development",
    params: {
      backswing_knee_flexion_deg: { low: 82, high: 115 },
      plant_knee_flexion_deg: { low: 28, high: 52 },
      trunk_forward_lean_deg: { low: 3, high: 21 },
      hip_rotation_deg: { low: 33, high: 62 },
      approach_angle_deg: { low: 25, high: 46 },
      foot_speed_mps: { low: 15, high: 23 },
      contact_time_ms: { low: 7, high: 15 },
      follow_through_angle_deg: { low: 75, high: 112 }
    }
  },
  
  // U18+ Standard
  {
    movementName: "Instep Shot – Power",
    ageBand: "U18+",
    level: "Standard",
    params: {
      backswing_knee_flexion_deg: { low: 87, high: 115 },
      plant_knee_flexion_deg: { low: 30, high: 50 },
      trunk_forward_lean_deg: { low: 6, high: 19 },
      hip_rotation_deg: { low: 36, high: 59 },
      approach_angle_deg: { low: 30, high: 45 },
      foot_speed_mps: { low: 17, high: 24 },
      contact_time_ms: { low: 8, high: 12 },
      follow_through_angle_deg: { low: 82, high: 110 }
    }
  },
  
  // U18+ Elite
  {
    movementName: "Instep Shot – Power",
    ageBand: "U18+",
    level: "Elite",
    params: {
      backswing_knee_flexion_deg: { low: 90, high: 115 },
      plant_knee_flexion_deg: { low: 32, high: 48 },
      trunk_forward_lean_deg: { low: 8, high: 18 },
      hip_rotation_deg: { low: 40, high: 58 },
      approach_angle_deg: { low: 32, high: 45 },
      foot_speed_mps: { low: 19, high: 26 },
      contact_time_ms: { low: 8, high: 11 },
      follow_through_angle_deg: { low: 88, high: 110 }
    }
  },
  
  // ============================================================
  // DRIVEN PASS – LONG BALL
  // ============================================================
  
  // U9–U11 Development
  {
    movementName: "Driven Pass – Long Ball",
    ageBand: "U9–U11",
    level: "Development",
    params: {
      backswing_knee_flexion_deg: { low: 65, high: 105 },
      plant_knee_flexion_deg: { low: 18, high: 50 },
      trunk_forward_lean_deg: { low: -5, high: 18 },
      hip_rotation_deg: { low: 20, high: 60 },
      approach_angle_deg: { low: 12, high: 42 },
      foot_speed_mps: { low: 10, high: 17 },
      contact_time_ms: { low: 6, high: 20 },
      follow_through_angle_deg: { low: 55, high: 110 }
    }
  },
  
  // U9–U11 Standard
  {
    movementName: "Driven Pass – Long Ball",
    ageBand: "U9–U11",
    level: "Standard",
    params: {
      backswing_knee_flexion_deg: { low: 70, high: 105 },
      plant_knee_flexion_deg: { low: 20, high: 48 },
      trunk_forward_lean_deg: { low: -2, high: 16 },
      hip_rotation_deg: { low: 25, high: 58 },
      approach_angle_deg: { low: 15, high: 40 },
      foot_speed_mps: { low: 11, high: 19 },
      contact_time_ms: { low: 7, high: 18 },
      follow_through_angle_deg: { low: 60, high: 108 }
    }
  },
  
  // U9–U11 Elite
  {
    movementName: "Driven Pass – Long Ball",
    ageBand: "U9–U11",
    level: "Elite",
    params: {
      backswing_knee_flexion_deg: { low: 75, high: 105 },
      plant_knee_flexion_deg: { low: 22, high: 45 },
      trunk_forward_lean_deg: { low: 0, high: 15 },
      hip_rotation_deg: { low: 28, high: 55 },
      approach_angle_deg: { low: 18, high: 40 },
      foot_speed_mps: { low: 12, high: 20 },
      contact_time_ms: { low: 8, high: 16 },
      follow_through_angle_deg: { low: 65, high: 105 }
    }
  },
  
  // U12–U14 Development
  {
    movementName: "Driven Pass – Long Ball",
    ageBand: "U12–U14",
    level: "Development",
    params: {
      backswing_knee_flexion_deg: { low: 70, high: 108 },
      plant_knee_flexion_deg: { low: 20, high: 50 },
      trunk_forward_lean_deg: { low: -3, high: 17 },
      hip_rotation_deg: { low: 23, high: 60 },
      approach_angle_deg: { low: 14, high: 42 },
      foot_speed_mps: { low: 11, high: 19 },
      contact_time_ms: { low: 7, high: 19 },
      follow_through_angle_deg: { low: 58, high: 110 }
    }
  },
  
  // U12–U14 Standard
  {
    movementName: "Driven Pass – Long Ball",
    ageBand: "U12–U14",
    level: "Standard",
    params: {
      backswing_knee_flexion_deg: { low: 72, high: 106 },
      plant_knee_flexion_deg: { low: 22, high: 46 },
      trunk_forward_lean_deg: { low: -1, high: 15 },
      hip_rotation_deg: { low: 27, high: 56 },
      approach_angle_deg: { low: 18, high: 40 },
      foot_speed_mps: { low: 12, high: 20 },
      contact_time_ms: { low: 8, high: 17 },
      follow_through_angle_deg: { low: 63, high: 106 }
    }
  },
  
  // U12–U14 Elite
  {
    movementName: "Driven Pass – Long Ball",
    ageBand: "U12–U14",
    level: "Elite",
    params: {
      backswing_knee_flexion_deg: { low: 75, high: 105 },
      plant_knee_flexion_deg: { low: 24, high: 44 },
      trunk_forward_lean_deg: { low: 0, high: 15 },
      hip_rotation_deg: { low: 30, high: 55 },
      approach_angle_deg: { low: 20, high: 40 },
      foot_speed_mps: { low: 13, high: 21 },
      contact_time_ms: { low: 9, high: 15 },
      follow_through_angle_deg: { low: 68, high: 105 }
    }
  },
  
  // U15–U17 Development
  {
    movementName: "Driven Pass – Long Ball",
    ageBand: "U15–U17",
    level: "Development",
    params: {
      backswing_knee_flexion_deg: { low: 70, high: 108 },
      plant_knee_flexion_deg: { low: 20, high: 50 },
      trunk_forward_lean_deg: { low: -2, high: 17 },
      hip_rotation_deg: { low: 25, high: 60 },
      approach_angle_deg: { low: 15, high: 42 },
      foot_speed_mps: { low: 12, high: 20 },
      contact_time_ms: { low: 8, high: 18 },
      follow_through_angle_deg: { low: 60, high: 108 }
    }
  },
  
  // U15–U17 Standard (baseline)
  {
    movementName: "Driven Pass – Long Ball",
    ageBand: "U15–U17",
    level: "Standard",
    params: {
      backswing_knee_flexion_deg: { low: 75, high: 105 },
      plant_knee_flexion_deg: { low: 25, high: 45 },
      trunk_forward_lean_deg: { low: 0, high: 15 },
      hip_rotation_deg: { low: 30, high: 55 },
      approach_angle_deg: { low: 20, high: 40 },
      foot_speed_mps: { low: 14, high: 21 },
      contact_time_ms: { low: 9, high: 14 },
      follow_through_angle_deg: { low: 70, high: 105 }
    }
  },
  
  // U15–U17 Elite
  {
    movementName: "Driven Pass – Long Ball",
    ageBand: "U15–U17",
    level: "Elite",
    params: {
      backswing_knee_flexion_deg: { low: 78, high: 105 },
      plant_knee_flexion_deg: { low: 27, high: 43 },
      trunk_forward_lean_deg: { low: 2, high: 14 },
      hip_rotation_deg: { low: 33, high: 53 },
      approach_angle_deg: { low: 22, high: 40 },
      foot_speed_mps: { low: 15, high: 22 },
      contact_time_ms: { low: 9, high: 13 },
      follow_through_angle_deg: { low: 73, high: 105 }
    }
  },
  
  // U18+ Development
  {
    movementName: "Driven Pass – Long Ball",
    ageBand: "U18+",
    level: "Development",
    params: {
      backswing_knee_flexion_deg: { low: 72, high: 106 },
      plant_knee_flexion_deg: { low: 23, high: 47 },
      trunk_forward_lean_deg: { low: -1, high: 16 },
      hip_rotation_deg: { low: 28, high: 57 },
      approach_angle_deg: { low: 18, high: 41 },
      foot_speed_mps: { low: 13, high: 21 },
      contact_time_ms: { low: 8, high: 16 },
      follow_through_angle_deg: { low: 65, high: 106 }
    }
  },
  
  // U18+ Standard
  {
    movementName: "Driven Pass – Long Ball",
    ageBand: "U18+",
    level: "Standard",
    params: {
      backswing_knee_flexion_deg: { low: 77, high: 105 },
      plant_knee_flexion_deg: { low: 26, high: 44 },
      trunk_forward_lean_deg: { low: 1, high: 15 },
      hip_rotation_deg: { low: 32, high: 54 },
      approach_angle_deg: { low: 20, high: 40 },
      foot_speed_mps: { low: 15, high: 22 },
      contact_time_ms: { low: 9, high: 14 },
      follow_through_angle_deg: { low: 72, high: 105 }
    }
  },
  
  // U18+ Elite
  {
    movementName: "Driven Pass – Long Ball",
    ageBand: "U18+",
    level: "Elite",
    params: {
      backswing_knee_flexion_deg: { low: 80, high: 105 },
      plant_knee_flexion_deg: { low: 28, high: 42 },
      trunk_forward_lean_deg: { low: 3, high: 14 },
      hip_rotation_deg: { low: 35, high: 52 },
      approach_angle_deg: { low: 22, high: 40 },
      foot_speed_mps: { low: 16, high: 23 },
      contact_time_ms: { low: 9, high: 13 },
      follow_through_angle_deg: { low: 75, high: 105 }
    }
  },
  
  // ============================================================
  // SHORT PUSH PASS – SIDE FOOT
  // ============================================================
  
  // U9–U11 Development
  {
    movementName: "Short Push Pass – Side Foot",
    ageBand: "U9–U11",
    level: "Development",
    params: {
      backswing_knee_flexion_deg: { low: 30, high: 85 },
      plant_knee_flexion_deg: { low: 15, high: 45 },
      trunk_forward_lean_deg: { low: -10, high: 12 },
      hip_rotation_deg: { low: 10, high: 40 },
      approach_angle_deg: { low: 5, high: 35 },
      foot_speed_mps: { low: 5, high: 12 },
      contact_time_ms: { low: 8, high: 20 },
      follow_through_angle_deg: { low: 30, high: 85 }
    }
  },
  
  // U9–U11 Standard
  {
    movementName: "Short Push Pass – Side Foot",
    ageBand: "U9–U11",
    level: "Standard",
    params: {
      backswing_knee_flexion_deg: { low: 35, high: 80 },
      plant_knee_flexion_deg: { low: 18, high: 42 },
      trunk_forward_lean_deg: { low: -8, high: 10 },
      hip_rotation_deg: { low: 12, high: 38 },
      approach_angle_deg: { low: 8, high: 32 },
      foot_speed_mps: { low: 6, high: 14 },
      contact_time_ms: { low: 9, high: 18 },
      follow_through_angle_deg: { low: 35, high: 80 }
    }
  },
  
  // U9–U11 Elite
  {
    movementName: "Short Push Pass – Side Foot",
    ageBand: "U9–U11",
    level: "Elite",
    params: {
      backswing_knee_flexion_deg: { low: 40, high: 80 },
      plant_knee_flexion_deg: { low: 20, high: 40 },
      trunk_forward_lean_deg: { low: -5, high: 10 },
      hip_rotation_deg: { low: 15, high: 35 },
      approach_angle_deg: { low: 10, high: 30 },
      foot_speed_mps: { low: 7, high: 15 },
      contact_time_ms: { low: 10, high: 16 },
      follow_through_angle_deg: { low: 40, high: 80 }
    }
  },
  
  // U12–U14 Development
  {
    movementName: "Short Push Pass – Side Foot",
    ageBand: "U12–U14",
    level: "Development",
    params: {
      backswing_knee_flexion_deg: { low: 33, high: 82 },
      plant_knee_flexion_deg: { low: 16, high: 44 },
      trunk_forward_lean_deg: { low: -9, high: 11 },
      hip_rotation_deg: { low: 11, high: 39 },
      approach_angle_deg: { low: 6, high: 33 },
      foot_speed_mps: { low: 6, high: 13 },
      contact_time_ms: { low: 9, high: 19 },
      follow_through_angle_deg: { low: 32, high: 82 }
    }
  },
  
  // U12–U14 Standard
  {
    movementName: "Short Push Pass – Side Foot",
    ageBand: "U12–U14",
    level: "Standard",
    params: {
      backswing_knee_flexion_deg: { low: 38, high: 80 },
      plant_knee_flexion_deg: { low: 19, high: 41 },
      trunk_forward_lean_deg: { low: -7, high: 10 },
      hip_rotation_deg: { low: 13, high: 36 },
      approach_angle_deg: { low: 9, high: 30 },
      foot_speed_mps: { low: 7, high: 14 },
      contact_time_ms: { low: 10, high: 17 },
      follow_through_angle_deg: { low: 38, high: 80 }
    }
  },
  
  // U12–U14 Elite
  {
    movementName: "Short Push Pass – Side Foot",
    ageBand: "U12–U14",
    level: "Elite",
    params: {
      backswing_knee_flexion_deg: { low: 40, high: 80 },
      plant_knee_flexion_deg: { low: 20, high: 40 },
      trunk_forward_lean_deg: { low: -5, high: 10 },
      hip_rotation_deg: { low: 15, high: 35 },
      approach_angle_deg: { low: 10, high: 30 },
      foot_speed_mps: { low: 8, high: 15 },
      contact_time_ms: { low: 10, high: 16 },
      follow_through_angle_deg: { low: 40, high: 80 }
    }
  },
  
  // U15–U17 Development
  {
    movementName: "Short Push Pass – Side Foot",
    ageBand: "U15–U17",
    level: "Development",
    params: {
      backswing_knee_flexion_deg: { low: 35, high: 82 },
      plant_knee_flexion_deg: { low: 17, high: 43 },
      trunk_forward_lean_deg: { low: -8, high: 11 },
      hip_rotation_deg: { low: 12, high: 38 },
      approach_angle_deg: { low: 7, high: 32 },
      foot_speed_mps: { low: 6, high: 14 },
      contact_time_ms: { low: 9, high: 18 },
      follow_through_angle_deg: { low: 35, high: 82 }
    }
  },
  
  // U15–U17 Standard (baseline)
  {
    movementName: "Short Push Pass – Side Foot",
    ageBand: "U15–U17",
    level: "Standard",
    params: {
      backswing_knee_flexion_deg: { low: 40, high: 80 },
      plant_knee_flexion_deg: { low: 20, high: 40 },
      trunk_forward_lean_deg: { low: -5, high: 10 },
      hip_rotation_deg: { low: 15, high: 35 },
      approach_angle_deg: { low: 10, high: 30 },
      foot_speed_mps: { low: 8, high: 14 },
      contact_time_ms: { low: 10, high: 16 },
      follow_through_angle_deg: { low: 40, high: 80 }
    }
  },
  
  // U15–U17 Elite
  {
    movementName: "Short Push Pass – Side Foot",
    ageBand: "U15–U17",
    level: "Elite",
    params: {
      backswing_knee_flexion_deg: { low: 42, high: 80 },
      plant_knee_flexion_deg: { low: 22, high: 38 },
      trunk_forward_lean_deg: { low: -3, high: 10 },
      hip_rotation_deg: { low: 17, high: 33 },
      approach_angle_deg: { low: 12, high: 30 },
      foot_speed_mps: { low: 9, high: 15 },
      contact_time_ms: { low: 10, high: 15 },
      follow_through_angle_deg: { low: 42, high: 80 }
    }
  },
  
  // U18+ Development
  {
    movementName: "Short Push Pass – Side Foot",
    ageBand: "U18+",
    level: "Development",
    params: {
      backswing_knee_flexion_deg: { low: 37, high: 81 },
      plant_knee_flexion_deg: { low: 18, high: 41 },
      trunk_forward_lean_deg: { low: -7, high: 10 },
      hip_rotation_deg: { low: 13, high: 36 },
      approach_angle_deg: { low: 8, high: 31 },
      foot_speed_mps: { low: 7, high: 14 },
      contact_time_ms: { low: 9, high: 17 },
      follow_through_angle_deg: { low: 37, high: 81 }
    }
  },
  
  // U18+ Standard
  {
    movementName: "Short Push Pass – Side Foot",
    ageBand: "U18+",
    level: "Standard",
    params: {
      backswing_knee_flexion_deg: { low: 41, high: 80 },
      plant_knee_flexion_deg: { low: 21, high: 39 },
      trunk_forward_lean_deg: { low: -4, high: 10 },
      hip_rotation_deg: { low: 16, high: 34 },
      approach_angle_deg: { low: 11, high: 30 },
      foot_speed_mps: { low: 8, high: 15 },
      contact_time_ms: { low: 10, high: 16 },
      follow_through_angle_deg: { low: 41, high: 80 }
    }
  },
  
  // U18+ Elite
  {
    movementName: "Short Push Pass – Side Foot",
    ageBand: "U18+",
    level: "Elite",
    params: {
      backswing_knee_flexion_deg: { low: 43, high: 80 },
      plant_knee_flexion_deg: { low: 23, high: 37 },
      trunk_forward_lean_deg: { low: -2, high: 10 },
      hip_rotation_deg: { low: 18, high: 32 },
      approach_angle_deg: { low: 13, high: 30 },
      foot_speed_mps: { low: 9, high: 16 },
      contact_time_ms: { low: 10, high: 15 },
      follow_through_angle_deg: { low: 43, high: 80 }
    }
  },
  
  // ============================================================
  // GOALKEEPER DISTRIBUTION – PUNT
  // ============================================================
  
  // U9–U11 Development
  {
    movementName: "Goalkeeper Distribution – Punt",
    ageBand: "U9–U11",
    level: "Development",
    params: {
      backswing_knee_flexion_deg: { low: 70, high: 125 },
      plant_knee_flexion_deg: { low: 18, high: 50 },
      trunk_forward_lean_deg: { low: -15, high: 12 },
      hip_rotation_deg: { low: 18, high: 55 },
      approach_angle_deg: { low: 5, high: 40 },
      foot_speed_mps: { low: 12, high: 20 },
      contact_time_ms: { low: 5, high: 20 },
      follow_through_angle_deg: { low: 75, high: 135 }
    }
  },
  
  // U9–U11 Standard
  {
    movementName: "Goalkeeper Distribution – Punt",
    ageBand: "U9–U11",
    level: "Standard",
    params: {
      backswing_knee_flexion_deg: { low: 80, high: 125 },
      plant_knee_flexion_deg: { low: 20, high: 48 },
      trunk_forward_lean_deg: { low: -12, high: 10 },
      hip_rotation_deg: { low: 22, high: 52 },
      approach_angle_deg: { low: 8, high: 38 },
      foot_speed_mps: { low: 13, high: 22 },
      contact_time_ms: { low: 6, high: 18 },
      follow_through_angle_deg: { low: 85, high: 135 }
    }
  },
  
  // U9–U11 Elite
  {
    movementName: "Goalkeeper Distribution – Punt",
    ageBand: "U9–U11",
    level: "Elite",
    params: {
      backswing_knee_flexion_deg: { low: 85, high: 125 },
      plant_knee_flexion_deg: { low: 22, high: 45 },
      trunk_forward_lean_deg: { low: -10, high: 10 },
      hip_rotation_deg: { low: 25, high: 50 },
      approach_angle_deg: { low: 10, high: 35 },
      foot_speed_mps: { low: 14, high: 24 },
      contact_time_ms: { low: 7, high: 16 },
      follow_through_angle_deg: { low: 90, high: 135 }
    }
  },
  
  // U12–U14 Development
  {
    movementName: "Goalkeeper Distribution – Punt",
    ageBand: "U12–U14",
    level: "Development",
    params: {
      backswing_knee_flexion_deg: { low: 75, high: 125 },
      plant_knee_flexion_deg: { low: 19, high: 50 },
      trunk_forward_lean_deg: { low: -13, high: 11 },
      hip_rotation_deg: { low: 20, high: 53 },
      approach_angle_deg: { low: 6, high: 39 },
      foot_speed_mps: { low: 13, high: 21 },
      contact_time_ms: { low: 6, high: 19 },
      follow_through_angle_deg: { low: 80, high: 135 }
    }
  },
  
  // U12–U14 Standard
  {
    movementName: "Goalkeeper Distribution – Punt",
    ageBand: "U12–U14",
    level: "Standard",
    params: {
      backswing_knee_flexion_deg: { low: 82, high: 125 },
      plant_knee_flexion_deg: { low: 22, high: 46 },
      trunk_forward_lean_deg: { low: -11, high: 10 },
      hip_rotation_deg: { low: 23, high: 50 },
      approach_angle_deg: { low: 9, high: 36 },
      foot_speed_mps: { low: 14, high: 23 },
      contact_time_ms: { low: 7, high: 17 },
      follow_through_angle_deg: { low: 88, high: 135 }
    }
  },
  
  // U12–U14 Elite
  {
    movementName: "Goalkeeper Distribution – Punt",
    ageBand: "U12–U14",
    level: "Elite",
    params: {
      backswing_knee_flexion_deg: { low: 87, high: 125 },
      plant_knee_flexion_deg: { low: 24, high: 44 },
      trunk_forward_lean_deg: { low: -9, high: 10 },
      hip_rotation_deg: { low: 26, high: 48 },
      approach_angle_deg: { low: 12, high: 35 },
      foot_speed_mps: { low: 15, high: 24 },
      contact_time_ms: { low: 8, high: 15 },
      follow_through_angle_deg: { low: 92, high: 135 }
    }
  },
  
  // U15–U17 Development
  {
    movementName: "Goalkeeper Distribution – Punt",
    ageBand: "U15–U17",
    level: "Development",
    params: {
      backswing_knee_flexion_deg: { low: 80, high: 125 },
      plant_knee_flexion_deg: { low: 20, high: 50 },
      trunk_forward_lean_deg: { low: -12, high: 11 },
      hip_rotation_deg: { low: 22, high: 53 },
      approach_angle_deg: { low: 7, high: 38 },
      foot_speed_mps: { low: 14, high: 22 },
      contact_time_ms: { low: 7, high: 18 },
      follow_through_angle_deg: { low: 85, high: 135 }
    }
  },
  
  // U15–U17 Standard (baseline)
  {
    movementName: "Goalkeeper Distribution – Punt",
    ageBand: "U15–U17",
    level: "Standard",
    params: {
      backswing_knee_flexion_deg: { low: 85, high: 120 },
      plant_knee_flexion_deg: { low: 25, high: 45 },
      trunk_forward_lean_deg: { low: -10, high: 10 },
      hip_rotation_deg: { low: 25, high: 50 },
      approach_angle_deg: { low: 10, high: 35 },
      foot_speed_mps: { low: 16, high: 24 },
      contact_time_ms: { low: 8, high: 13 },
      follow_through_angle_deg: { low: 90, high: 130 }
    }
  },
  
  // U15–U17 Elite
  {
    movementName: "Goalkeeper Distribution – Punt",
    ageBand: "U15–U17",
    level: "Elite",
    params: {
      backswing_knee_flexion_deg: { low: 90, high: 120 },
      plant_knee_flexion_deg: { low: 27, high: 43 },
      trunk_forward_lean_deg: { low: -8, high: 10 },
      hip_rotation_deg: { low: 28, high: 48 },
      approach_angle_deg: { low: 12, high: 35 },
      foot_speed_mps: { low: 18, high: 25 },
      contact_time_ms: { low: 8, high: 12 },
      follow_through_angle_deg: { low: 95, high: 130 }
    }
  },
  
  // U18+ Development
  {
    movementName: "Goalkeeper Distribution – Punt",
    ageBand: "U18+",
    level: "Development",
    params: {
      backswing_knee_flexion_deg: { low: 82, high: 123 },
      plant_knee_flexion_deg: { low: 23, high: 47 },
      trunk_forward_lean_deg: { low: -11, high: 10 },
      hip_rotation_deg: { low: 24, high: 51 },
      approach_angle_deg: { low: 8, high: 36 },
      foot_speed_mps: { low: 15, high: 23 },
      contact_time_ms: { low: 7, high: 16 },
      follow_through_angle_deg: { low: 87, high: 132 }
    }
  },
  
  // U18+ Standard
  {
    movementName: "Goalkeeper Distribution – Punt",
    ageBand: "U18+",
    level: "Standard",
    params: {
      backswing_knee_flexion_deg: { low: 88, high: 120 },
      plant_knee_flexion_deg: { low: 26, high: 44 },
      trunk_forward_lean_deg: { low: -9, high: 10 },
      hip_rotation_deg: { low: 27, high: 49 },
      approach_angle_deg: { low: 11, high: 35 },
      foot_speed_mps: { low: 17, high: 24 },
      contact_time_ms: { low: 8, high: 13 },
      follow_through_angle_deg: { low: 92, high: 130 }
    }
  },
  
  // U18+ Elite
  {
    movementName: "Goalkeeper Distribution – Punt",
    ageBand: "U18+",
    level: "Elite",
    params: {
      backswing_knee_flexion_deg: { low: 93, high: 120 },
      plant_knee_flexion_deg: { low: 28, high: 42 },
      trunk_forward_lean_deg: { low: -7, high: 10 },
      hip_rotation_deg: { low: 30, high: 47 },
      approach_angle_deg: { low: 13, high: 35 },
      foot_speed_mps: { low: 19, high: 26 },
      contact_time_ms: { low: 8, high: 12 },
      follow_through_angle_deg: { low: 97, high: 130 }
    }
  }
];

/**
 * Helper function to find a benchmark set by movement, age band, and level
 */
export function findBenchmark(
  movementName: string,
  ageBand: string,
  level: "Development" | "Standard" | "Elite"
): BenchmarkSet | undefined {
  return BENCHMARK_SETS.find(
    (b) =>
      b.movementName === movementName &&
      b.ageBand === ageBand &&
      b.level === level
  );
}

/**
 * Extract unique values for filtering
 */
export const MOVEMENT_TYPES = Array.from(
  new Set(BENCHMARK_SETS.map((b) => b.movementName))
);

export const AGE_BANDS = Array.from(
  new Set(BENCHMARK_SETS.map((b) => b.ageBand))
);

export const LEVELS: ("Development" | "Standard" | "Elite")[] = [
  "Development",
  "Standard",
  "Elite"
];

